import { Filter, GlProgram, Texture, type Container } from 'pixi.js'
import fragment from './nightShader.frag?raw'
import { NIGHT_PRESETS, type NightParams, type NightPresetId } from './nightPresets'
import {
  adaptCurve,
  alreadyGraded,
  computeSceneStats,
  darkSourceLift,
  sceneCompensation,
  type SceneStats,
} from './sceneStats'
import { buildNightMask } from './masks'

/**
 * The night grade, as one GPU pass over the property photograph.
 *
 * WHAT THIS REPLACES: a CPU pass that rebuilt a full graded bitmap every time
 * the dial moved, took the better part of three seconds to do it, and then
 * cross faded two images. Dragging the slider was unusable and the grade was
 * quantised to twenty steps because anything finer was too slow to compute.
 *
 * Here the photograph is uploaded once and every parameter is a uniform, so
 * moving the dial is a handful of float writes and the next frame is already
 * correct. Nothing is read back from the GPU and no image analysis happens
 * during interaction.
 *
 * THE LAYER GUARANTEE. This filter is attached to the photo layer and to
 * nothing else. The bulbs, their glow, the wires, the measurement line, the
 * drawing handles, the selection outlines, the logo and the overlay are all
 * SIBLINGS of that layer in the scene graph, not children of it, so the grade
 * cannot reach them - not by tuning, not by accident, not by a future edit
 * that forgets. That is a structural guarantee rather than a promise, which is
 * why the filter goes on the layer and not on the world container.
 */

const vertex = `
in vec2 aPosition;
out vec2 vTextureCoord;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

vec4 filterVertexPosition( void ) {
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    return vec4(position, 0.0, 1.0);
}

vec2 filterTextureCoord( void ) {
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void) {
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
}
`

/** Everything the shader needs, in one place, all live. */
export interface NightUniforms extends NightParams {
  strength: number
  mono: boolean
}

export class NightRenderer {
  readonly filter: Filter
  private mask: Texture = Texture.WHITE
  private stats: SceneStats | null = null
  private comp = 0
  private backoff = 0
  private darkLift = 0
  private preset: NightPresetId = 'dusk'
  private overrides: Partial<NightParams> = {}
  private strength = 0
  private mono = false
  private debugMagenta = false
  /** null = internal preset mode (tests, debug panel). Set = production. */
  private intensity: number | null = null
  /**
   * A test or the debug panel has taken control.
   *
   * The stage re-applies the production intensity from the document on every
   * redraw. That is right for the app and it fought the harness: an internal
   * preset render was overwritten within a frame and the filter came back
   * reporting inactive. While this is set the redraw keeps its hands off.
   */
  private override = false

  constructor() {
    this.filter = new Filter({
      glProgram: GlProgram.from({ vertex, fragment, name: 'night-grade' }),
      resources: {
        nightUniforms: {
          uNightStrength: { value: 0, type: 'f32' },
          uShadowKeep: { value: 0.36, type: 'f32' },
          uHighlightKeep: { value: 0.035, type: 'f32' },
          uKnee: { value: 1.5, type: 'f32' },
          uExposureComp: { value: 0, type: 'f32' },
          uChromaStrength: { value: 0.38, type: 'f32' },
          uChromaResponse: { value: 0.55, type: 'f32' },
          uCoolShadows: { value: 0.18, type: 'f32' },
          uTint: { value: 0.03, type: 'f32' },
          uContrast: { value: 0.1, type: 'f32' },
          uBlackFloor: { value: 0.0012, type: 'f32' },
          uTexturePreservation: { value: 0.22, type: 'f32' },
          uVignette: { value: 0.06, type: 'f32' },
          uSkyExtra: { value: -1.5, type: 'f32' },
          uSkyFacing: { value: 0.45, type: 'f32' },
          uChromaDarkening: { value: 0.35, type: 'f32' },
          uMono: { value: 0, type: 'f32' },
          uDebugMagenta: { value: 0, type: 'f32' },
        },
        uMask: Texture.WHITE.source,
      },
    })
    // Nothing here blurs or spreads, so the filter needs no padding around the
    // sprite. Padding would also break the mask lookup, which relies on the
    // output frame being exactly the photo.
    this.filter.padding = 0
    this.filter.enabled = false
  }

  /**
   * A new photograph. This is the ONLY expensive call, and it happens on load,
   * never during interaction.
   */
  setSource(source: HTMLImageElement | HTMLCanvasElement | null): void {
    this.disposeMask()
    if (!source) {
      this.stats = null
      this.comp = 0
      this.mask = Texture.WHITE
      this.setUniform('uMask', Texture.WHITE.source)
      this.apply()
      return
    }
    this.stats = computeSceneStats(source)
    this.comp = sceneCompensation(this.stats)
    this.backoff = alreadyGraded(this.stats)
    this.darkLift = darkSourceLift(this.stats, this.backoff)
    const built = buildNightMask(source)
    this.mask = built.texture
    this.setUniform('uMask', built.texture.source)
    this.apply()
  }

  getStats(): SceneStats | null {
    return this.stats
  }

  getCompensation(): number {
    return this.comp
  }

  /** How much of the grade this photo does not need, 0 to 1. */
  getBackoff(): number {
    return this.backoff
  }

  /** How much extra lift this photo's property earns, 0 to 1. */
  getDarkLift(): number {
    return this.darkLift
  }

  /**
   * The strength actually reaching the shader.
   *
   * A photograph already at the target gets very little of the grade however
   * far the contractor pushes the dial, because the honest answer for a shot
   * taken at dusk is "this is already the picture you asked for". Scaled
   * rather than switched, so nothing jumps as a photo gets closer to it.
   */
  getEffectiveStrength(): number {
    return this.effectiveStrength()
  }

  private effectiveStrength(): number {
    return this.strength * (1 - 0.92 * this.backoff)
  }

  /** The resolved values actually in the shader right now, for the debug panel. */
  getResolved(): NightParams {
    let base: NightParams = { ...NIGHT_PRESETS[this.preset] }
    if (this.intensity !== null) {
      // Past the join, the grade deepens rather than merely being mixed in
      // harder. Every parameter lerps, so nothing jumps at the crossover.
      const k = Math.max(0, Math.min(1, (this.intensity - 0.7) / 0.3))
      const a = NIGHT_PRESETS.dusk
      const b = NIGHT_PRESETS.night
      const lerped = { ...a }
      for (const key of Object.keys(a) as (keyof NightParams)[]) {
        lerped[key] = a[key] + (b[key] - a[key]) * k
      }
      base = lerped
    }
    /*
     * A DARK PROPERTY GETS LIFTED. THE SKY DOES NOT FOLLOW IT.
     *
     * Scaling both keeps by one factor scales the curve by exactly that
     * factor and leaves its shape alone, so this is the same lever as the
     * global brightness and it cannot flatten anything or bring daylight
     * highlights back. skyExtra then takes the sky down by the identical
     * amount, so a photo with a bright sky over a shaded house gets its house
     * lifted and its sky left where it was.
     *
     * The global brightness is locked. This is per photograph, and it is zero
     * for a photograph that does not need it.
     */
    const lift = 1 + 0.25 * this.darkLift
    if (lift > 1.001) {
      base = {
        ...base,
        shadowKeep: base.shadowKeep * lift,
        highlightKeep: base.highlightKeep * lift,
        skyExtra: base.skyExtra - Math.log2(lift),
      }
    }
    base = { ...base, ...this.overrides }
    const adapted = adaptCurve(base.shadowKeep, base.highlightKeep, this.comp)
    return { ...base, ...adapted }
  }

  setPreset(id: NightPresetId): void {
    this.preset = id
    this.apply()
  }

  /** Drive one internal preset at an exact strength, ignoring the document. */
  setTestPreset(id: NightPresetId, strength: number): void {
    this.override = true
    this.preset = id
    this.intensity = null
    this.strength = Math.max(0, Math.min(1, strength))
    this.apply()
  }

  /** Hand control back to the document. */
  clearTestOverride(): void {
    this.override = false
    this.apply()
  }

  isInternal(): boolean {
    return this.override
  }

  /**
   * The value actually sitting in the shader right now.
   *
   * Exposed so a test can assert that what it asked for is what is being
   * rendered, rather than trusting that a setter had the effect its name
   * implies. It did not, once: a strength setter that wrote to the document
   * while the redraw was ignoring the document left the uniform at zero.
   */
  getUniformStrength(): number {
    const g = (this.filter.resources as Record<string, unknown>).nightUniforms as
      | { uniforms: Record<string, number> }
      | undefined
    return g?.uniforms?.uNightStrength ?? -1
  }

  /** Live tuning. Anything set here wins over the preset until it is cleared. */
  setOverrides(o: Partial<NightParams>): void {
    this.overrides = { ...this.overrides, ...o }
    this.apply()
  }

  clearOverrides(): void {
    this.overrides = {}
    this.apply()
  }

  setStrength(v: number): void {
    this.intensity = null
    this.strength = Math.max(0, Math.min(1, v))
    this.apply()
  }

  /**
   * THE PRODUCTION CONTROL. One number, 0 to 1, and it has to behave.
   *
   * Mixing the finished grade against the original by this number does not
   * work: measured across the thirteen benchmark photographs, three quarters
   * of the blue hour treatment still reads as a slightly dim afternoon. Every
   * check passed and every picture still looked like daytime, which is the
   * whole failure this project keeps rediscovering.
   *
   * So the dial does two things in sequence, the way a real evening does.
   *
   *   0 .. 0.7   the blue hour grade fades in to its full strength
   *   0.7 .. 1   the grade itself deepens, from dusk toward night
   *
   * The default lands just past the join, so pressing Night gives the full
   * blue hour look and the slider has somewhere to go in BOTH directions -
   * lighter, back toward the photograph as shot, or darker into real night.
   * The contractor never sees a preset; there is one slider and it gets
   * lighter to the left and darker to the right, which is the only mental
   * model this control should need.
   */
  setIntensity(t: number): void {
    this.override = false
    const v = Math.max(0, Math.min(1, t))
    this.intensity = v
    this.strength = Math.min(1, v / 0.7)
    this.apply()
  }

  setMono(on: boolean): void {
    this.mono = on
    this.apply()
  }

  /** Proof that this shader is the thing drawing to the screen. See the note
   *  on uDebugMagenta in nightShader.frag. */
  setDebugMagenta(on: boolean): void {
    this.debugMagenta = on
    this.apply()
  }

  /** Whether the filter is actually doing anything right now. */
  isActive(): boolean {
    return this.filter.enabled
  }

  /**
   * Push the resolved values into the uniform group.
   *
   * This is the whole per interaction cost: sixteen float writes. The filter
   * switches itself off entirely at zero strength so an ungraded photo pays
   * nothing at all - not even a pass.
   */
  private apply(): void {
    const p = this.getResolved()
    this.filter.enabled = this.effectiveStrength() > 0.001 || this.debugMagenta
    this.setUniform('uNightStrength', this.effectiveStrength())
    this.setUniform('uShadowKeep', p.shadowKeep)
    this.setUniform('uHighlightKeep', p.highlightKeep)
    this.setUniform('uKnee', p.knee)
    this.setUniform('uExposureComp', p.exposureComp)
    this.setUniform('uChromaStrength', p.chromaStrength)
    this.setUniform('uChromaResponse', p.chromaResponse)
    this.setUniform('uCoolShadows', p.coolShadows)
    this.setUniform('uTint', p.tint)
    this.setUniform('uContrast', p.contrast)
    this.setUniform('uBlackFloor', p.blackFloor)
    this.setUniform('uTexturePreservation', p.texturePreservation)
    this.setUniform('uVignette', p.vignette)
    this.setUniform('uSkyExtra', p.skyExtra)
    this.setUniform('uSkyFacing', p.skyFacing)
    this.setUniform('uChromaDarkening', p.chromaDarkening)
    this.setUniform('uMono', this.mono ? 1 : 0)
    this.setUniform('uDebugMagenta', this.debugMagenta ? 1 : 0)
  }

  private setUniform(name: string, value: unknown): void {
    const res = this.filter.resources as Record<string, unknown>
    if (name === 'uMask') {
      res.uMask = value
      return
    }
    const group = res.nightUniforms as { uniforms: Record<string, unknown> } | undefined
    if (group?.uniforms && name in group.uniforms) group.uniforms[name] = value
  }

  /**
   * Attach to the photo layer, and only ever the photo layer.
   *
   * Called with the container that holds the property photograph. Everything
   * else in the scene is a sibling, which is what makes it impossible for this
   * to touch the lights.
   */
  attachTo(photoLayer: Container): void {
    photoLayer.filters = [this.filter]
  }

  static detach(photoLayer: Container): void {
    photoLayer.filters = []
  }

  private disposeMask(): void {
    if (this.mask && this.mask !== Texture.WHITE) this.mask.destroy(true)
  }

  destroy(): void {
    this.disposeMask()
    this.filter.destroy()
  }
}
